import { useState, useRef, useEffect } from "react";
import { X, Save, Upload, Trash2, Loader2, Crosshair, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { api } from "../lib/api";
import { absoluteUrl } from "../lib/media";
import { toast } from "sonner";

/**
 * PhotoEditor — mini atelier photo pour une recette.
 * - Recadrage par point focal (click sur l'image)
 * - Zoom (slider) et alt-text
 * - Upload / remplacer / supprimer
 * - Aperçu miniature (menu QR) + aperçu grand (fiche) en temps réel
 */
export default function PhotoEditor({ recipe, onClose, onSaved }) {
  const [meta, setMeta] = useState(() => recipe.photo_meta || {
    alt_text: recipe.name || "", focal_x: 50, focal_y: 50, zoom: 1.0, show_on_menu: true,
  });
  const [photoUrl, setPhotoUrl] = useState(recipe.photo_url || null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const imgWrapRef = useRef();

  useEffect(() => {
    setMeta(recipe.photo_meta || { alt_text: recipe.name || "", focal_x: 50, focal_y: 50, zoom: 1.0, show_on_menu: true });
    setPhotoUrl(recipe.photo_url || null);
  }, [recipe.id]);

  const onImgClick = (e) => {
    const r = imgWrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setMeta((m) => ({ ...m, focal_x: Math.round(x), focal_y: Math.round(y) }));
  };

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const res = await api.uploadRecipePhoto(recipe.id, f);
      setPhotoUrl(res.photo_url);
      if (res.photo_meta) setMeta(res.photo_meta);
      toast.success("Photo téléversée");
    } catch { toast.error("Échec de l'envoi de la photo"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    setBusy(true);
    try {
      await api.deleteRecipePhoto(recipe.id);
      setPhotoUrl(null);
      toast.success("Photo supprimée");
    } catch { toast.error("Échec de la suppression"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.updateRecipePhotoMeta(recipe.id, meta);
      toast.success("Recadrage enregistré");
      onSaved?.({ photo_url: photoUrl, photo_meta: res.photo_meta });
      onClose?.();
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setBusy(false); }
  };

  const src = photoUrl ? absoluteUrl(photoUrl) : null;
  const bgPos = `${meta.focal_x}% ${meta.focal_y}%`;
  const bgSize = `${100 * (meta.zoom || 1)}%`;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[880px] w-[95vw] p-0 gap-0 overflow-hidden" data-testid="photo-editor-dialog">
        <DialogHeader className="px-5 py-3.5 border-b border-[#CDD7E3] bg-[#F4F5EF]">
          <DialogTitle className="text-[15px] flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#1769E0]" />
            Atelier photo · <span className="font-normal text-[#5E6875]">{recipe.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          {/* Left — interactive edit */}
          <div className="p-5 border-r border-[#CDD7E3]">
            <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-2 flex items-center gap-1.5">
              <Crosshair className="w-3.5 h-3.5" /> Cliquez sur la photo pour définir le point focal
            </div>
            <div
              ref={imgWrapRef}
              onClick={src ? onImgClick : undefined}
              className={`relative w-full aspect-[4/3] rounded-md overflow-hidden border border-[#CDD7E3] bg-[#F4F5EF] ${src ? "cursor-crosshair" : ""}`}
              data-testid="photo-editor-canvas"
            >
              {src ? (
                <>
                  <img
                    src={src}
                    alt=""
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="absolute inset-0 w-full h-full object-cover opacity-40 pointer-events-none"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ backgroundImage: `url(${src})`, backgroundSize: bgSize, backgroundPosition: bgPos, backgroundRepeat: "no-repeat" }}
                    aria-hidden
                  />
                  <div
                    className="absolute w-6 h-6 rounded-full border-2 border-white shadow-[0_0_0_2px_#0B1F33] pointer-events-none"
                    style={{ left: `calc(${meta.focal_x}% - 12px)`, top: `calc(${meta.focal_y}% - 12px)`, background: "rgba(255,159,28,0.85)" }}
                    data-testid="photo-focal-marker"
                  />
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#5E6875]">
                  <Camera className="w-8 h-8 text-[#CDD7E3]" />
                  <p className="text-[13px]">Aucune photo pour l'instant.</p>
                </div>
              )}
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[12px]">Zoom</Label>
                  <span className="text-[11.5px] font-mono-num text-[#5E6875]">{Math.round((meta.zoom || 1) * 100)}%</span>
                </div>
                <input
                  type="range" min="1" max="2.5" step="0.05"
                  value={meta.zoom || 1}
                  onChange={(e) => setMeta({ ...meta, zoom: Number(e.target.value) })}
                  data-testid="photo-zoom-slider"
                  className="w-full accent-[#FF9F1C]"
                />
              </div>
              <div>
                <Label className="text-[12px]">Texte alternatif (accessibilité)</Label>
                <Input
                  value={meta.alt_text || ""}
                  onChange={(e) => setMeta({ ...meta, alt_text: e.target.value })}
                  placeholder="Photo du plat…"
                  data-testid="photo-alt"
                  className="!h-9 text-[13px]"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!meta.show_on_menu}
                  onCheckedChange={(v) => setMeta({ ...meta, show_on_menu: v })}
                  data-testid="photo-show-on-menu"
                />
                <Label className="text-[12px]">Afficher la photo sur le menu QR</Label>
              </div>
            </div>
          </div>

          {/* Right — previews */}
          <div className="p-5 bg-[#F4F5EF]">
            <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-2">Aperçus</div>
            <div className="space-y-4">
              <div>
                <div className="text-[11px] text-[#5E6875] mb-1">Fiche plat (grand)</div>
                <div
                  className="w-full aspect-[16/10] rounded-md border border-[#CDD7E3] bg-white overflow-hidden"
                  style={src ? { backgroundImage: `url(${src})`, backgroundSize: bgSize, backgroundPosition: bgPos } : {}}
                  data-testid="photo-preview-card"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] text-[#5E6875] mb-1">Miniature liste</div>
                  <div
                    className="w-16 h-16 rounded-md border border-[#CDD7E3] bg-white overflow-hidden"
                    style={src ? { backgroundImage: `url(${src})`, backgroundSize: bgSize, backgroundPosition: bgPos } : {}}
                    data-testid="photo-preview-thumb"
                  />
                </div>
                <div>
                  <div className="text-[11px] text-[#5E6875] mb-1">Menu QR (carré)</div>
                  <div
                    className="w-20 h-20 rounded-xl border border-[#CDD7E3] bg-white overflow-hidden"
                    style={src ? { backgroundImage: `url(${src})`, backgroundSize: bgSize, backgroundPosition: bgPos } : {}}
                    data-testid="photo-preview-qr"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="photo-upload-btn"
                className="!h-9 bg-[#0B1F33] hover:bg-[#1a3050] text-white gap-1.5"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {src ? "Remplacer" : "Téléverser"}
              </Button>
              {src && (
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={remove}
                  data-testid="photo-delete-btn"
                  className="!h-9 gap-1.5 text-[#D84343] border-[#F5C7C7] hover:bg-[#FDECEC]"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Supprimer
                </Button>
              )}
              <input
                ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])} data-testid="photo-file-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-[#CDD7E3] bg-white">
          <Button variant="outline" onClick={onClose} data-testid="photo-editor-cancel">Annuler</Button>
          <Button
            onClick={save}
            disabled={busy || !src}
            data-testid="photo-editor-save"
            className="btn-orange !h-10 border-0"
          >
            <Save className="w-4 h-4" /> Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
